import React, { useState } from "react";
import { Button, Card, Space, Tag } from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  Game,
  GameWithTags,
  QuizSituation,
} from "../../../services/gameQuiz/gameQuiz.types";
import { GameTagDefinition } from "../../../services/gameTags/gameTags.types";
import { createEmptyGame, GAME_TYPE_LABELS } from "./gameTypes";
import GameItemEditor from "./GameItemEditor";
import { situationName } from "./examSituations";

type Props = {
  games: GameWithTags[];
  onChange: (games: GameWithTags[]) => void;
  generateAudio?: (text: string) => Promise<string>;
  generateAudioWithBlanks?: (text: string) => Promise<string>;
  bloomsTags: GameTagDefinition[];
  pisaTags: GameTagDefinition[];
  isExam?: boolean;
  situations?: QuizSituation[];
};

const GamesBuilder: React.FC<Props> = ({
  games,
  onChange,
  generateAudio,
  generateAudioWithBlanks,
  bloomsTags,
  pisaTags,
  isExam = false,
  situations = [],
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleCollapse = (gameId: string) => {
    setExpanded((prev) => ({ ...prev, [gameId]: !prev[gameId] }));
  };

  const handleAdd = (type: Game["type"]) => {
    const newGame = createEmptyGame(type) as GameWithTags;
    onChange([...games, newGame]);
    setExpanded((prev) => ({ ...prev, [newGame.id]: true }));
  };

  const updateGame = (game: GameWithTags) => {
    onChange(games.map((g) => (g.id === game.id ? game : g)));
  };

  const removeGame = (gameId: string) => {
    if (window.confirm("¿Eliminar esta pregunta?")) {
      onChange(games.filter((g) => g.id !== gameId));
    }
  };

  const moveGame = (index: number, dir: -1 | 1) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= games.length) return;
    const next = [...games];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    onChange(next);
  };

  return (
    <div>
      {games.map((game, idx) => (
        <Card
          key={game.id}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space
              style={{ cursor: "pointer" }}
              onClick={() => toggleCollapse(game.id)}
            >
              <Button
                type="text"
                size="small"
                icon={
                  expanded[game.id] ? <DownOutlined /> : <RightOutlined />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCollapse(game.id);
                }}
              />
              <span>#{idx + 1}</span>
              <Tag>{GAME_TYPE_LABELS[game.type]}</Tag>
              {isExam && game.points !== undefined && (
                <Tag color="blue">{game.points} pts</Tag>
              )}
              {isExam && game.situation_id && (
                <Tag color="purple">
                  {situationName(situations, game.situation_id)}
                </Tag>
              )}
              <span style={{ color: "#888", fontWeight: 400 }}>
                {game.question || "(sin pregunta)"}
              </span>
            </Space>
          }
          extra={
            <Space>
              <Button
                type="text"
                icon={<UpOutlined />}
                onClick={() => moveGame(idx, -1)}
                disabled={idx === 0}
              />
              <Button
                type="text"
                icon={<DownOutlined />}
                onClick={() => moveGame(idx, 1)}
                disabled={idx === games.length - 1}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeGame(game.id)}
              />
            </Space>
          }
        >
          {expanded[game.id] && (
            <GameItemEditor
              game={game}
              onChange={updateGame}
              generateAudio={generateAudio}
              generateAudioWithBlanks={generateAudioWithBlanks}
              bloomsTags={bloomsTags}
              pisaTags={pisaTags}
              isExam={isExam}
              situations={situations}
            />
          )}
        </Card>
      ))}

      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: "1px solid var(--bo-card-border)",
        }}
      >
        <div style={{ marginBottom: 8, color: "var(--bo-text-secondary)" }}>
          Agregar nueva pregunta
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {Object.entries(GAME_TYPE_LABELS).map(([value, label]) => (
            <Button
              key={value}
              onClick={() => handleAdd(value as Game["type"])}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GamesBuilder;
